import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button, Progress } from 'reactstrap';
import { getCookie } from 'views/utils';
import useBackgroundJob from 'components/useBackgroundJob';
import { formatEta } from 'components/jobEta';

const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

// How many of the newest activities a sync looks at. 0 = all of them.
const SYNC_LIMIT = 0;

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
}

function formatTime(iso) {
  return iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
}

// Starts a sync in the background (the API answers with a ticket), keeps the ticket in the browser and follows
// its progress. On a reload the stored ticket is checked, so a running sync shows up again.
function SyncPanel() {
  const [status, setStatus] = useState(null);
  const mounted = useRef(true);

  const loadStatus = useCallback(() => {
    axios
      .get(`${resource_api_url}/api/Sync_Status`, { headers: { Authorization: getCookie('stratava_token') } })
      .then((res) => mounted.current && setStatus(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadStatus();
    return () => {
      mounted.current = false;
    };
  }, [loadStatus]);

  const { job, active, starting, error, start } = useBackgroundJob({
    ticketKey: 'stravalytics_sync_ticket',
    startPath: '/api/Sync_Start',
    startBody: { limit: SYNC_LIMIT },
    onFinished: loadStatus,
  });

  const percent = job && job.total ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
  const eta = formatEta(job);

  let text;
  if (error) {
    text = { kind: 'warn', value: error };
  } else if (active && job.status === 'rate_limited') {
    text = {
      kind: 'warn',
      value: `Waiting for Strava's rate limit. ${job.done} of ${job.total} done; it continues by itself around ${formatTime(job.nextRunAt)}. You can close this page.`,
    };
  } else if (active) {
    text = {
      kind: 'info',
      value: job.total == null
        ? 'Checking which activities are new...'
        : `Loading in the background: ${job.done} of ${job.total} (${percent}%)${eta ? `, ${eta}` : ''}. You can close this page.`,
    };
  } else if (job && job.status === 'done') {
    const n = job.loaded + job.noStreams;
    text = {
      kind: 'ok',
      value: n > 0
        ? `Done: ${n} new ${n === 1 ? 'activity' : 'activities'} saved${job.failed ? `, ${job.failed} could not be loaded` : ''}.`
        : 'Already up to date.',
    };
  } else if (job && job.status === 'failed') {
    text = { kind: 'warn', value: `The sync stopped: ${job.error || 'unknown error'}. Start it again to continue.` };
  }
  const color = { ok: 'var(--forge-primary)', warn: 'var(--forge-warning)', info: 'var(--forge-text-muted)' };

  return (
    <div className="forge-auth-panel" style={{ marginBottom: '24px' }}>
      <div className="forge-subhead" style={{ marginBottom: '8px' }}>
        <i className="fas fa-cloud-download-alt" style={{ marginRight: '8px', color: 'var(--forge-primary)' }} />
        SAVE YOUR ACTIVITIES
      </div>
      <p style={{ color: 'var(--forge-text-muted)', fontFamily: 'var(--font-body)', fontSize: '0.875rem', marginBottom: '12px' }}>
        {status && status.count > 0
          ? `${status.count} saved (${formatDate(status.oldest)} to ${formatDate(status.newest)}). A sync only loads what is new.`
          : `Saves your activities with all their data, so they can be analysed without asking Strava again.`}
      </p>
      <Button color="info" size="sm" disabled={starting || active} onClick={start}>
        {starting || active ? (
          <>
            <i className="fas fa-circle-notch fa-spin" style={{ marginRight: '8px' }} />
            Syncing...
          </>
        ) : (
          SYNC_LIMIT ? `Sync new activities (latest ${SYNC_LIMIT})` : 'Sync all activities'
        )}
      </Button>
      {active && job.total > 0 && (
        <Progress value={percent} style={{ marginTop: '12px', height: '6px' }} color={job.status === 'rate_limited' ? 'warning' : 'info'} />
      )}
      {text && (
        <p style={{ marginTop: '12px', marginBottom: 0, color: color[text.kind], fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}>
          {text.value}
        </p>
      )}
    </div>
  );
}

export default SyncPanel;
